/**
 * PortfolioWidget - Portfolio overview with performance chart and metrics
 *
 * Layout matches reference design:
 * - Header with total value, return badge, period selector
 * - Line chart for performance
 * - Metrics grid (3 columns x 2 rows)
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsTreemap from 'highcharts/modules/treemap';
import dayjs from 'dayjs';
import { detectDarkMode, observeDarkMode, WIDGET_BACKGROUND, getPriceColor } from './chartTheme';
import { useContainerWidth } from './hooks/useContainerWidth';

// Initialize Highcharts treemap module
if (typeof HighchartsTreemap === 'function') {
    HighchartsTreemap(Highcharts);
}

// View mode options
const VIEW_MODES = [
    { value: 'cards', label: 'Tổng quan', icon: 'grid' },
    { value: 'treemap', label: 'Treemap', icon: 'treemap' },
    { value: 'table', label: 'Bảng', icon: 'table' },
];

// Calculate Time-Weighted Rate of Return (TWRR)
function calculateTWRR(returns) {
    if (!returns || returns.length === 0) return 0;
    let cumulativeProduct = 1;
    for (let i = 0; i < returns.length; i++) {
        if (returns[i] !== null && returns[i] !== undefined) {
            cumulativeProduct *= 1 + returns[i] / 100;
        }
    }
    return (cumulativeProduct - 1) * 100; // Return as percentage
}

// Filter data by period
function filterByPeriod(data, period) {
    if (!data || data.length === 0) return [];

    const now = dayjs();
    let filterDate;

    switch (period) {
        case '1W':
            filterDate = now.subtract(1, 'week');
            break;
        case '1M':
            filterDate = now.subtract(1, 'month');
            break;
        case '3M':
            filterDate = now.subtract(3, 'month');
            break;
        case '6M':
            filterDate = now.subtract(6, 'month');
            break;
        case '1Y':
            filterDate = now.subtract(1, 'year');
            break;
        case 'YTD':
            filterDate = dayjs().startOf('year');
            break;
        case 'ALL':
        default:
            return data;
    }

    return data.filter(item => dayjs(item.date).isAfter(filterDate));
}

// Format number with Vietnamese abbreviations
function formatCompactNumber(value) {
    if (value === null || value === undefined) return '-';
    const absValue = Math.abs(value);
    if (absValue >= 1e12) return `${(value / 1e12).toFixed(2)} nghìn tỷ`;
    if (absValue >= 1e9) return `${(value / 1e9).toFixed(2)} tỷ`;
    if (absValue >= 1e6) return `${(value / 1e6).toFixed(2)} triệu`;
    if (absValue >= 1e3) return `${(value / 1e3).toFixed(1)}K`;
    return value.toLocaleString('vi-VN');
}

// Format currency in VND
function formatCurrency(value) {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('vi-VN').format(Math.round(value));
}

// Period options
const PERIOD_OPTIONS = [
    { value: '1W', label: '7 ngày' },
    { value: '1M', label: '30 ngày' },
    { value: '3M', label: '3 tháng' },
    { value: '6M', label: '6 tháng' },
    { value: '1Y', label: '1 năm' },
    { value: 'YTD', label: 'YTD' },
    { value: 'ALL', label: 'Tất cả' },
];

/**
 * Main PortfolioWidget
 */
export default function PortfolioWidget({ data: initialData, config }) {
    const [isDarkMode, setIsDarkMode] = useState(detectDarkMode);
    const [selectedPeriod, setSelectedPeriod] = useState('1Y');
    const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
    const [viewMode, setViewMode] = useState('cards'); // 'cards', 'treemap', 'table'
    const chartRef = useRef(null);
    const treemapRef = useRef(null);
    const dropdownRef = useRef(null);

    // Use reusable hook for responsive container width detection
    const { containerRef, containerWidth, isMobile, isCompact } = useContainerWidth(500);

    useEffect(() => {
        return observeDarkMode(setIsDarkMode);
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowPeriodDropdown(false);
            }
        };
        if (showPeriodDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPeriodDropdown]);

    // Parse portfolio data from widget proxy response (camelCase matching API)
    const portfolioData = useMemo(() => {
        if (!initialData) return null;

        // Widget proxy: { data: { portfolio: {...} } }
        const portfolio = initialData.data?.portfolio || initialData.portfolio || initialData;

        return {
            id: portfolio.id,
            name: portfolio.name || 'Danh mục',
            type: portfolio.type,
            totalValue: portfolio.totalPortfolioValue || 0,
            stockValue: portfolio.totalPortfolioStock || 0,
            cashRemaining: portfolio.cashRemainingAmount || 0,
            totalReturnValue: portfolio.totalReturnValue || 0,
            totalReturnPct: portfolio.totalReturnValuePct || 0,
            returnTodayValue: portfolio.totalReturnValueChange || 0,
            returnTodayPct: portfolio.totalReturnValueChangePct || 0,
            holdingCount: portfolio.stockCount || 0,
            // Period returns
            return1W: portfolio.totalReturn1w,
            returnYTD: portfolio.totalReturnYtd,
            return1Y: portfolio.totalReturn1y,
            totalReturn: portfolio.totalReturn,
            cagr1Y: portfolio.cagr1y,
            // Additional fields
            totalDividendCash: portfolio.totalDividendCash,
            totalDividendCashPct: portfolio.totalDividendCashPct,
            totalReturnValueIntraday: portfolio.totalReturnValueIntraday,
            totalReturnValueIntradayPct: portfolio.totalReturnValueIntradayPct,
            // Warnings
            returnWarningCashs: portfolio.returnWarningCashs || [],
        };
    }, [initialData]);

    // Parse holdings data for best performer
    const holdingsData = useMemo(() => {
        if (!initialData) return [];

        const portfolio = initialData.data?.portfolio || initialData.portfolio || initialData;
        return portfolio.stocks || [];
    }, [initialData]);

    // Find best performer stock
    const bestPerformer = useMemo(() => {
        if (!holdingsData || holdingsData.length === 0) return null;
        return holdingsData.reduce((best, stock) => {
            if (!best || (stock.totalReturnValuePct || 0) > (best.totalReturnValuePct || 0)) {
                return stock;
            }
            return best;
        }, null);
    }, [holdingsData]);

    // Parse return value history for chart (camelCase matching API)
    const returnValueDetails = useMemo(() => {
        if (!initialData) return [];

        const portfolio = initialData.data?.portfolio || initialData.portfolio || initialData;
        const returnDetails = portfolio.returnValueDetails || [];

        if (Array.isArray(returnDetails) && returnDetails.length > 0) {
            return returnDetails.map(item => ({
                date: item.date,
                totalValue: item.totalValue || item.value || 0,
                dailyReturn: item.dailyReturn,
                vnindexDailyReturn: item.vnindexDailyReturn,
                unitReturn: item.unitReturn,
                vnIndexReturn: item.vnIndexReturn,
            }));
        }

        return [];
    }, [initialData]);

    // Filter data by selected period and calculate TWRR for chart
    const chartData = useMemo(() => {
        const filteredData = filterByPeriod(returnValueDetails, selectedPeriod);
        if (filteredData.length === 0) return { portfolio: [], vnindex: [], categories: [], fullDates: [] };

        // Calculate cumulative TWRR for each data point
        const portfolioSeries = [];
        const vnindexSeries = [];
        const categories = [];
        const fullDates = [];

        filteredData.forEach((item, index) => {
            // Extract daily returns up to this point
            const portfolioDailyReturns = filteredData.slice(0, index + 1).map(d => d.dailyReturn);
            const vnindexDailyReturns = filteredData.slice(0, index + 1).map(d => d.vnindexDailyReturn);

            // Calculate cumulative TWRR
            const portfolioTWRR = calculateTWRR(portfolioDailyReturns);
            const vnindexTWRR = calculateTWRR(vnindexDailyReturns);

            portfolioSeries.push(portfolioTWRR);
            vnindexSeries.push(vnindexTWRR);

            // Format date as DD/MM for x-axis labels (compact)
            // and DD/MM/YYYY for tooltip (full date)
            if (item.date) {
                const parts = item.date.split('-');
                if (parts.length >= 3) {
                    categories.push(`${parts[2]}/${parts[1]}`);
                    fullDates.push(`${parts[2]}/${parts[1]}/${parts[0]}`);
                } else {
                    categories.push(item.date);
                    fullDates.push(item.date);
                }
            }
        });

        return { portfolio: portfolioSeries, vnindex: vnindexSeries, categories, fullDates };
    }, [returnValueDetails, selectedPeriod]);

    // Chart options - dual line chart (Portfolio vs VN-Index)
    const chartOptions = useMemo(() => {
        if (!chartData.portfolio || chartData.portfolio.length === 0) return null;

        const { categories, portfolio, vnindex, fullDates } = chartData;

        // Determine if portfolio is outperforming
        const lastPortfolioValue = portfolio[portfolio.length - 1] || 0;
        const lastVnindexValue = vnindex[vnindex.length - 1] || 0;
        const isOutperforming = lastPortfolioValue > lastVnindexValue;

        // Calculate Y-axis min/max with 10% padding for better scaling
        const allValues = [...portfolio, ...vnindex].filter(v => v !== null && v !== undefined);
        const dataMin = Math.min(...allValues);
        const dataMax = Math.max(...allValues);
        const range = dataMax - dataMin;
        const padding = range * 0.1 || 1; // 10% padding, minimum 1
        const yMin = Math.floor((dataMin - padding) * 10) / 10;
        const yMax = Math.ceil((dataMax + padding) * 10) / 10;

        return {
            chart: {
                height: isMobile ? 200 : 240,
                backgroundColor: 'transparent',
                spacing: [10, 10, 10, 10],
            },
            title: { text: null },
            credits: { enabled: false },
            legend: {
                enabled: false, // Legend disabled - using bottom summary instead
            },
            xAxis: {
                categories,
                tickPositioner: function() {
                    const interval = Math.ceil(categories.length / (isMobile ? 4 : 6));
                    const positions = [];
                    // Start from first interval position, skip 0 and last
                    for (let i = interval; i < categories.length - 1; i += interval) {
                        positions.push(i);
                    }
                    return positions;
                },
                tickmarkPlacement: 'on',
                labels: {
                    style: {
                        color: isDarkMode ? '#9ca3af' : '#6b7280',
                        fontSize: '10px',
                    },
                },
                lineWidth: 0,
                tickWidth: 0,
                gridLineWidth: 1,
                gridLineColor: isDarkMode ? '#374151' : '#f3f4f6',
            },
            yAxis: {
                title: { text: null },
                min: yMin,
                max: yMax,
                labels: {
                    enabled: true,
                    align: 'right',
                    x: -5,
                    style: {
                        color: isDarkMode ? '#9ca3af' : '#6b7280',
                        fontSize: '10px',
                    },
                    formatter: function() {
                        return this.value.toFixed(1) + '%';
                    },
                },
                gridLineWidth: 0,
                tickAmount: 5,
                opposite: true,
                plotLines: [{
                    color: isDarkMode ? '#4b5563' : '#d1d5db',
                    width: 1,
                    value: 0,
                    dashStyle: 'Dash',
                }],
            },
            tooltip: {
                shared: true,
                useHTML: true,
                backgroundColor: 'transparent',
                borderWidth: 0,
                shadow: false,
                padding: 0,
                formatter: function() {
                    const bgColor = isDarkMode ? '#1f2937' : '#ffffff';
                    const textColor = isDarkMode ? '#f3f4f6' : '#1f2937';

                    // Get full date with year from fullDates array
                    const pointIndex = this.points && this.points.length > 0 ? this.points[0].point.index : 0;
                    const dateLabel = fullDates && fullDates[pointIndex] ? fullDates[pointIndex] : this.x;

                    let seriesHTML = '';
                    this.points.forEach(point => {
                        const color = point.color;
                        const value = point.y.toFixed(2);
                        seriesHTML += `<div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
                            <span style="color:${color};font-size:14px;">●</span>
                            <span style="font-size:12px;color:${textColor}">${point.series.name}:</span>
                            <span style="font-size:12px;font-weight:600;color:${textColor}">${value}%</span>
                        </div>`;
                    });

                    return `<div style="background:${bgColor};border-radius:8px;padding:10px;min-width:140px;box-shadow:0 4px 16px rgba(0,0,0,0.2);">
                        <div style="font-size:12px;font-weight:600;color:${textColor};margin-bottom:8px;">${dateLabel}</div>
                        ${seriesHTML}
                    </div>`;
                },
            },
            plotOptions: {
                series: {
                    lineWidth: 2,
                    marker: {
                        enabled: false,
                        states: {
                            hover: { enabled: true, radius: 4 },
                        },
                    },
                },
                areaspline: {
                    fillColor: {
                        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                        stops: [
                            [0, isOutperforming
                                ? (isDarkMode ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.25)')
                                : (isDarkMode ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.25)')],
                            [1, isOutperforming
                                ? (isDarkMode ? 'rgba(34, 197, 94, 0.02)' : 'rgba(34, 197, 94, 0.02)')
                                : (isDarkMode ? 'rgba(239, 68, 68, 0.02)' : 'rgba(239, 68, 68, 0.02)')]
                        ]
                    },
                    threshold: null,
                },
            },
            series: [
                {
                    type: 'areaspline',
                    name: 'Danh mục',
                    data: portfolio,
                    color: isOutperforming ? '#22c55e' : '#ef4444',
                    lineWidth: 2,
                },
                {
                    type: 'line',
                    name: 'VN-Index',
                    data: vnindex,
                    color: isDarkMode ? '#60a5fa' : '#3b82f6',
                    dashStyle: 'Dash',
                    lineWidth: 1.5,
                },
            ],
        };
    }, [chartData, isDarkMode, isMobile]);

    // Treemap chart options for holdings visualization
    const treemapOptions = useMemo(() => {
        if (!holdingsData || holdingsData.length === 0) return null;

        // Calculate total value for percentage
        const totalValue = holdingsData.reduce((sum, stock) => sum + (stock.valueEnd || 0), 0);

        // Minimum percentage threshold for visibility - larger on mobile for better readability
        // Desktop also needs reasonable minimum to ensure small stocks are visible when one stock dominates
        const MIN_DISPLAY_PCT = isMobile ? 12 : 4;

        // Limit number of stocks on mobile for better cell sizes
        const MAX_STOCKS_MOBILE = 8;
        const stocksToShow = isMobile
            ? [...holdingsData].sort((a, b) => (b.valueEnd || 0) - (a.valueEnd || 0)).slice(0, MAX_STOCKS_MOBILE)
            : holdingsData;

        // Gradient colors matching return chart area fill (lighter/subtle)
        const getColor = (returnPct) => {
            if (returnPct >= 0) {
                // Green gradient matching chart line (#22c55e) - lighter
                return {
                    linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                    stops: isDarkMode
                        ? [[0, 'rgba(34, 197, 94, 0.4)'], [1, 'rgba(34, 197, 94, 0.15)']]  // green-500 gradient dark
                        : [[0, 'rgba(34, 197, 94, 0.25)'], [1, 'rgba(34, 197, 94, 0.08)']] // green-500 gradient light
                };
            }
            // Red gradient matching chart line (#ef4444) - lighter
            return {
                linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                stops: isDarkMode
                    ? [[0, 'rgba(239, 68, 68, 0.4)'], [1, 'rgba(239, 68, 68, 0.15)']]  // red-500 gradient dark
                    : [[0, 'rgba(239, 68, 68, 0.25)'], [1, 'rgba(239, 68, 68, 0.08)']] // red-500 gradient light
            };
        };

        // Build treemap data with colors based on return
        const treemapData = stocksToShow.map(stock => {
            const value = stock.valueEnd || 0;
            const weight = totalValue > 0 ? (value / totalValue) * 100 : 0;
            const returnPct = stock.totalReturnValuePct || 0;
            const returnValue = stock.totalReturnValue || 0;
            const priceChange = stock.pricePctChgIntraDay || 0;
            const currentPrice = stock.price || stock.priceCloseIntraDay || 0;
            const costValue = stock.costValue || 0;

            // Ensure minimum size for small positions
            const displayWeight = Math.max(weight, MIN_DISPLAY_PCT);

            return {
                name: stock.ticker,
                companyName: stock.companyName || '',
                value: displayWeight,
                realValue: value,
                realWeight: weight,
                returnPct: returnPct,
                returnValue: returnValue,
                priceChange: priceChange,
                currentPrice: currentPrice,
                costValue: costValue,
                color: getColor(returnPct),
            };
        });

        // Keep consistent height - mobile uses inline layout for flat cells
        const treemapHeight = 200;

        return {
            chart: {
                type: 'treemap',
                height: treemapHeight,
                backgroundColor: 'transparent',
                spacing: [2, 2, 2, 2],
            },
            title: { text: null },
            credits: { enabled: false },
            tooltip: {
                outside: true,
                useHTML: true,
                backgroundColor: 'transparent',
                borderWidth: 0,
                shadow: false,
                padding: 0,
                formatter: function() {
                    const point = this.point;
                    const logoUrl = `https://finance.vietstock.vn/image/${point.name}`;
                    const priceChangeColor = point.priceChange >= 0 ? '#22c55e' : '#ef4444';
                    const returnColor = point.returnPct >= 0 ? '#22c55e' : '#ef4444';
                    const bgColor = isDarkMode ? '#1f2937' : '#ffffff';
                    const textColor = isDarkMode ? '#f3f4f6' : '#1f2937';
                    const subTextColor = isDarkMode ? '#9ca3af' : '#6b7280';
                    const borderColor = isDarkMode ? '#374151' : '#e5e7eb';

                    // Calculate return bar width (capped at 100%)
                    const returnBarWidth = Math.min(Math.abs(point.returnPct), 100);

                    const tooltipWidth = isMobile ? 200 : 240;

                    return `<div style="background:${bgColor};border-radius:12px;padding:${isMobile ? '10px' : '12px'};width:${tooltipWidth}px;box-shadow:0 4px 16px rgba(0,0,0,0.2);">
                        <!-- Header: Logo + Ticker + Company -->
                        <div style="display:flex;align-items:center;gap:${isMobile ? '8px' : '10px'};margin-bottom:${isMobile ? '8px' : '10px'};">
                            <img src="${logoUrl}" style="width:${isMobile ? '30px' : '36px'};height:${isMobile ? '30px' : '36px'};border-radius:50%;background:#f3f4f6;object-fit:contain;" onerror="this.style.display='none'"/>
                            <div style="flex:1;min-width:0;">
                                <div style="font-size:${isMobile ? '13px' : '14px'};font-weight:700;color:${textColor}">${point.name}</div>
                                <div style="font-size:${isMobile ? '10px' : '11px'};color:${subTextColor};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${point.companyName || ''}</div>
                            </div>
                        </div>

                        <!-- Price Section -->
                        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:${isMobile ? '6px' : '8px'};padding-bottom:${isMobile ? '6px' : '8px'};border-bottom:1px solid ${borderColor};">
                            <div>
                                <div style="font-size:${isMobile ? '10px' : '11px'};color:${subTextColor};">Giá hiện tại</div>
                                <div style="font-size:${isMobile ? '13px' : '15px'};font-weight:600;color:${textColor}">${formatCurrency(point.currentPrice)}</div>
                            </div>
                            <div style="text-align:right;">
                                <div style="font-size:${isMobile ? '11px' : '12px'};font-weight:600;color:${priceChangeColor}">
                                    ${point.priceChange >= 0 ? '▲' : '▼'} ${Math.abs(point.priceChange).toFixed(2)}%
                                </div>
                            </div>
                        </div>

                        <!-- Value Metrics -->
                        <div style="display:grid;grid-template-columns:1fr 1fr;gap:${isMobile ? '6px' : '8px'};margin-bottom:${isMobile ? '8px' : '10px'};">
                            <div>
                                <div style="font-size:${isMobile ? '9px' : '10px'};color:${subTextColor};">Giá trị thị trường</div>
                                <div style="font-size:${isMobile ? '11px' : '12px'};font-weight:500;color:${textColor}">${formatCompactNumber(point.realValue)}</div>
                            </div>
                            <div>
                                <div style="font-size:${isMobile ? '9px' : '10px'};color:${subTextColor};">Tỷ trọng</div>
                                <div style="font-size:${isMobile ? '11px' : '12px'};font-weight:500;color:${textColor}">${point.realWeight.toFixed(1)}%</div>
                            </div>
                            <div>
                                <div style="font-size:${isMobile ? '9px' : '10px'};color:${subTextColor};">Giá vốn</div>
                                <div style="font-size:${isMobile ? '11px' : '12px'};font-weight:500;color:${textColor}">${formatCompactNumber(point.costValue)}</div>
                            </div>
                            <div>
                                <div style="font-size:${isMobile ? '9px' : '10px'};color:${subTextColor};">Lợi nhuận</div>
                                <div style="font-size:${isMobile ? '11px' : '12px'};font-weight:600;color:${returnColor}">${point.returnPct >= 0 ? '+' : ''}${formatCompactNumber(point.returnValue)}</div>
                            </div>
                        </div>

                        <!-- Return Performance Bar -->
                        <div style="background:${isDarkMode ? '#374151' : '#f3f4f6'};border-radius:6px;padding:${isMobile ? '6px' : '8px'};">
                            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
                                <span style="font-size:${isMobile ? '9px' : '10px'};color:${subTextColor};">Hiệu suất đầu tư</span>
                                <span style="font-size:${isMobile ? '11px' : '12px'};font-weight:700;color:${returnColor}">${point.returnPct >= 0 ? '+' : ''}${point.returnPct.toFixed(2)}%</span>
                            </div>
                            <div style="height:${isMobile ? '5px' : '6px'};background:${isDarkMode ? '#4b5563' : '#e5e7eb'};border-radius:3px;overflow:hidden;">
                                <div style="height:100%;width:${returnBarWidth}%;background:${returnColor};border-radius:3px;"></div>
                            </div>
                        </div>
                    </div>`;
                },
            },
            plotOptions: {
                treemap: {
                    // Same layout for both mobile and desktop: squarified creates compact layout
                    layoutAlgorithm: 'squarified',
                    layoutStartingDirection: 'vertical',
                    dataLabels: {
                        enabled: true,
                        align: 'left',
                        verticalAlign: 'top',
                        padding: 4,
                        style: {
                            color: isDarkMode ? '#1f2937' : '#374151',
                            fontSize: '12px',
                            fontWeight: '600',
                            textOutline: 'none',
                        },
                        formatter: function() {
                            const point = this.point;
                            const weight = point.realWeight || 0;
                            const valueStr = formatCompactNumber(point.realValue);
                            const pctStr = weight.toFixed(1) + '%';

                            // Get actual cell dimensions from shapeArgs
                            const cellWidth = point.shapeArgs?.width || 100;
                            const cellHeight = point.shapeArgs?.height || 100;

                            // Determine cell size category based on dimensions
                            const isTinyCell = cellWidth < 50 || cellHeight < 40;
                            const isSmallCell = cellWidth < 80 || cellHeight < 60;
                            const isMediumCell = cellWidth < 120 || cellHeight < 80;

                            // Tiny cell: adapt layout based on aspect ratio
                            if (isTinyCell) {
                                if (cellHeight > cellWidth) {
                                    // Tall & narrow: stack vertically
                                    return `<div style="display:flex;flex-direction:column;gap:0px;">
                                        <span style="font-size:8px;font-weight:700;color:${isDarkMode ? '#1f2937' : '#374151'}">${point.name}</span>
                                        <span style="font-size:7px;font-weight:400;color:${isDarkMode ? '#4b5563' : '#6b7280'}">${pctStr}</span>
                                    </div>`;
                                }
                                // Wide & short: display inline
                                return `<span style="font-size:8px;font-weight:700;color:${isDarkMode ? '#1f2937' : '#374151'}">${point.name}</span> <span style="font-size:7px;font-weight:400;color:${isDarkMode ? '#4b5563' : '#6b7280'}">${pctStr}</span>`;
                            }

                            const logoUrl = `https://finance.vietstock.vn/image/${point.name}`;

                            if (isSmallCell) {
                                // Small cell: small logo + ticker + %
                                const logoSize = 18;
                                return `<div style="display:flex;flex-direction:column;align-items:flex-start;gap:1px;padding:2px;">
                                    <div style="width:${logoSize}px;height:${logoSize}px;flex-shrink:0;">
                                        <img src="${logoUrl}" style="width:100%;height:100%;border-radius:50%;background:#f3f4f6;object-fit:contain;" onerror="this.style.display='none'"/>
                                    </div>
                                    <span style="font-size:11px;font-weight:700;color:${isDarkMode ? '#1f2937' : '#374151'}">${point.name}</span>
                                    <span style="font-size:9px;font-weight:400;color:${isDarkMode ? '#4b5563' : '#6b7280'}">${pctStr}</span>
                                </div>`;
                            }

                            if (isMediumCell) {
                                // Medium cell: small logo + ticker + %
                                const logoSize = 20;
                                return `<div style="display:flex;flex-direction:column;align-items:flex-start;gap:2px;padding:4px;">
                                    <div style="width:${logoSize}px;height:${logoSize}px;flex-shrink:0;">
                                        <img src="${logoUrl}" style="width:100%;height:100%;border-radius:50%;background:#f3f4f6;object-fit:contain;" onerror="this.style.display='none'"/>
                                    </div>
                                    <span style="font-size:12px;font-weight:700;color:${isDarkMode ? '#1f2937' : '#374151'}">${point.name}</span>
                                    <span style="font-size:10px;font-weight:400;color:${isDarkMode ? '#4b5563' : '#6b7280'}">${pctStr}</span>
                                </div>`;
                            }

                            // Large cell: full display with logo + ticker + value + %
                            const scale = Math.min(Math.max(weight / 15, 0.8), 1.5);
                            const logoSize = Math.max(24, Math.round(28 * scale));
                            const tickerSize = Math.round(13 * scale);
                            const valueSize = Math.round(11 * scale);

                            return `<div style="display:flex;flex-direction:column;gap:2px;padding:4px;">
                                <div style="display:flex;align-items:center;gap:4px;">
                                    <div style="width:${logoSize}px;height:${logoSize}px;flex-shrink:0;">
                                        <img src="${logoUrl}" style="width:100%;height:100%;border-radius:50%;background:#f3f4f6;object-fit:contain;" onerror="this.style.display='none'"/>
                                    </div>
                                    <span style="font-size:${tickerSize}px;font-weight:700;color:${isDarkMode ? '#1f2937' : '#374151'}">${point.name}</span>
                                </div>
                                <span style="font-size:${valueSize}px;font-weight:400;color:${isDarkMode ? '#4b5563' : '#6b7280'}">${valueStr} ${pctStr}</span>
                            </div>`;
                        },
                        useHTML: true,
                    },
                    borderWidth: 4,
                    borderColor: isDarkMode ? '#1f2937' : '#ffffff',
                    borderRadius: 12,
                    states: {
                        hover: {
                            borderColor: isDarkMode ? '#374151' : '#e5e7eb',
                            brightness: 0,
                        },
                    },
                },
            },
            series: [{
                type: 'treemap',
                data: treemapData,
            }],
        };
    }, [holdingsData, isDarkMode, isMobile]);

    if (!portfolioData) {
        return (
            <div ref={containerRef} className="rounded-xl p-6 text-center text-gray-500 border border-gray-100 dark:border-gray-800"
                 style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>
                Không có dữ liệu danh mục
            </div>
        );
    }

    const isPositive = portfolioData.totalReturnPct >= 0;
    const periodLabel = PERIOD_OPTIONS.find(o => o.value === selectedPeriod)?.label || '30 ngày';

    return (
        <div ref={containerRef} className="space-y-3">
            {/* Card 1: Main Evaluation */}
            <div className="rounded-xl overflow-hidden border border-gray-100 dark:border-gray-800"
                 style={{ backgroundColor: isDarkMode ? WIDGET_BACKGROUND.dark : WIDGET_BACKGROUND.light }}>

                {/* Header */}
                <div className={isMobile ? 'px-2 pt-2 pb-1' : 'px-5 pt-5 pb-3'}>
                    <div className="flex items-start justify-between mb-1">
                        <div>
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                                {portfolioData.name}
                            </h3>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Tổng tài sản
                            </div>
                        </div>

                        {/* Period Selector Dropdown */}
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            >
                                {periodLabel}
                                <svg className={`w-3.5 h-3.5 transition-transform ${showPeriodDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {showPeriodDropdown && (
                                <div className="absolute top-full right-0 mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 min-w-[100px]">
                                    {PERIOD_OPTIONS.map(opt => (
                                        <button
                                            key={opt.value}
                                            onClick={() => {
                                                setSelectedPeriod(opt.value);
                                                setShowPeriodDropdown(false);
                                            }}
                                            className={`w-full px-3 py-1.5 text-xs text-left hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                                selectedPeriod === opt.value
                                                    ? 'text-blue-600 dark:text-blue-400 font-medium'
                                                    : 'text-gray-700 dark:text-gray-300'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Total Value */}
                    <div className="flex items-baseline gap-3 mt-2">
                        <span className={`${isMobile ? 'text-3xl' : 'text-4xl'} font-bold text-gray-900 dark:text-white tracking-tight`}>
                            {formatCurrency(portfolioData.totalValue)}
                        </span>

                        {/* Return badges */}
                        <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2 py-1 rounded-md ${
                                isPositive
                                    ? 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400'
                                    : 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400'
                            }`}>
                                {isPositive ? '▲' : '▼'}{Math.abs(portfolioData.totalReturnPct).toFixed(1)}%
                            </span>
                            <span className={`text-xs font-medium ${isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                {isPositive ? '+' : ''}{formatCompactNumber(portfolioData.totalReturnValue)}
                            </span>
                        </div>
                    </div>

                </div>

                {/* Chart */}
                {chartOptions ? (
                    <div className={isMobile ? 'px-1 pb-1' : 'px-1 pb-1'}>
                        <HighchartsReact
                            ref={chartRef}
                            highcharts={Highcharts}
                            options={chartOptions}
                        />
                        {/* Performance comparison summary */}
                        {chartData.portfolio?.length > 0 && (
                            <div className="flex items-center justify-center gap-4 mt-2 px-3">
                                <div className="flex items-center gap-1.5 text-xs">
                                    <span className={`inline-block w-3 h-0.5 rounded ${
                                        (chartData.portfolio[chartData.portfolio.length - 1] || 0) >= 0
                                            ? 'bg-green-500' : 'bg-red-500'
                                    }`}></span>
                                    <span className="text-gray-500 dark:text-gray-400">Danh mục:</span>
                                    <span className={`font-medium ${
                                        (chartData.portfolio[chartData.portfolio.length - 1] || 0) >= 0
                                            ? 'text-green-600 dark:text-green-400'
                                            : 'text-red-600 dark:text-red-400'
                                    }`}>
                                        {(chartData.portfolio[chartData.portfolio.length - 1] || 0) >= 0 ? '+' : ''}
                                        {(chartData.portfolio[chartData.portfolio.length - 1] || 0).toFixed(2)}%
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs">
                                    <span className="inline-block w-3 h-0.5 rounded bg-blue-500" style={{ borderStyle: 'dashed' }}></span>
                                    <span className="text-gray-500 dark:text-gray-400">VN-Index:</span>
                                    <span className={`font-medium ${
                                        (chartData.vnindex[chartData.vnindex.length - 1] || 0) >= 0
                                            ? 'text-green-600 dark:text-green-400'
                                            : 'text-red-600 dark:text-red-400'
                                    }`}>
                                        {(chartData.vnindex[chartData.vnindex.length - 1] || 0) >= 0 ? '+' : ''}
                                        {(chartData.vnindex[chartData.vnindex.length - 1] || 0).toFixed(2)}%
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="px-5 py-8 text-center text-gray-400 text-sm">
                        Chưa có dữ liệu hiệu suất đầu tư
                    </div>
                )}

                {/* View Mode Switcher */}
                <div className={`${isMobile ? 'px-2 py-1' : 'px-4 py-2'} flex items-center justify-end`}>
                    <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
                        {VIEW_MODES.map(mode => (
                            <button
                                key={mode.value}
                                onClick={() => setViewMode(mode.value)}
                                className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                                    viewMode === mode.value
                                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                                title={mode.label}
                            >
                                {mode.icon === 'grid' && (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                    </svg>
                                )}
                                {mode.icon === 'treemap' && (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                                    </svg>
                                )}
                                {mode.icon === 'table' && (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* View Mode Content */}
                {viewMode === 'cards' && (
                    <div className={`${isMobile ? 'overflow-x-auto' : ''}`}>
                        <div className={isMobile ? 'min-w-[420px]' : ''}>
                            {/* Period Returns Row - 3 columns with inset dividers */}
                            <div className="flex">
                                {/* Tổng lợi nhuận */}
                                <div className={`flex-1 ${isMobile ? 'px-3 py-2.5' : 'px-4 py-4'}`}>
                                    <div className={`text-gray-500 dark:text-gray-400 whitespace-nowrap ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'}`}>Tổng lợi nhuận</div>
                                    <div className={`font-bold whitespace-nowrap ${isMobile ? 'text-sm' : 'text-base'} ${(portfolioData.totalReturnValue || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                        {(portfolioData.totalReturnValue || 0) >= 0 ? '+' : ''}{formatCompactNumber(portfolioData.totalReturnValue || 0)}
                                    </div>
                                    <div className={`font-medium mt-0.5 whitespace-nowrap ${isMobile ? 'text-[10px]' : 'text-xs'} ${(portfolioData.totalReturnPct || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                        {(portfolioData.totalReturnPct || 0) >= 0 ? '+' : ''}{(portfolioData.totalReturnPct || 0).toFixed(2)}%
                                    </div>
                                </div>

                                {/* Từ đầu năm (YTD) */}
                                <div className={`flex-1 ${isMobile ? 'px-3 py-2.5' : 'px-4 py-4'}`}>
                                    <div className={`text-gray-500 dark:text-gray-400 whitespace-nowrap ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'}`}>Từ đầu năm</div>
                                    <div className={`font-bold whitespace-nowrap ${isMobile ? 'text-sm' : 'text-base'} ${(portfolioData.returnYTD || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                        {(portfolioData.returnYTD || 0) >= 0 ? '+' : ''}{(portfolioData.returnYTD || 0).toFixed(2)}%
                                    </div>
                                </div>

                                {/* Cổ phiếu tốt nhất */}
                                <div className={`flex-1 ${isMobile ? 'px-3 py-2.5' : 'px-4 py-4'}`}>
                                    <div className={`text-gray-500 dark:text-gray-400 whitespace-nowrap ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'}`}>Cổ phiếu tốt nhất</div>
                                    {bestPerformer ? (
                                        <div className={`flex items-center ${isMobile ? 'gap-1.5' : 'gap-2.5'}`}>
                                            <img
                                                src={`https://finance.vietstock.vn/image/${bestPerformer.ticker}`}
                                                alt={bestPerformer.ticker}
                                                className={`rounded-full object-contain bg-gray-100 dark:bg-gray-700 ${isMobile ? 'w-5 h-5' : 'w-8 h-8'}`}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'flex';
                                                }}
                                            />
                                            <div
                                                className={`rounded-full items-center justify-center font-bold text-white bg-blue-500 hidden ${isMobile ? 'w-5 h-5 text-[10px]' : 'w-8 h-8 text-xs'}`}
                                            >
                                                {bestPerformer.ticker?.charAt(0)}
                                            </div>
                                            <div>
                                                <div className={`font-semibold text-gray-900 dark:text-white ${isMobile ? 'text-xs' : 'text-sm'}`}>{bestPerformer.ticker}</div>
                                                <div className={`font-medium ${isMobile ? 'text-[10px]' : 'text-xs'} ${(bestPerformer.totalReturnValuePct || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                    {(bestPerformer.totalReturnValuePct || 0) >= 0 ? '+' : ''}{(bestPerformer.totalReturnValuePct || 0).toFixed(2)}%
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className={`text-gray-400 ${isMobile ? 'text-xs' : 'text-sm'}`}>-</div>
                                    )}
                                </div>
                            </div>

                            {/* Divider - inset for cleaner look */}
                            <div className={`${isMobile ? 'mx-3' : 'mx-4'} border-t border-gray-100 dark:border-gray-700`} />

                            {/* Row 2 */}
                            <div className="flex">
                                {/* Giá trị cổ phiếu */}
                                <div className={`flex-1 ${isMobile ? 'px-3 py-2.5' : 'px-4 py-4'}`}>
                                    <div className={`text-gray-500 dark:text-gray-400 whitespace-nowrap ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'}`}>Giá trị cổ phiếu</div>
                                    <div className={`font-bold text-gray-900 dark:text-white whitespace-nowrap ${isMobile ? 'text-sm' : 'text-base'}`}>
                                        {formatCompactNumber(portfolioData.stockValue)}
                                    </div>
                                </div>

                                {/* Tiền mặt */}
                                <div className={`flex-1 ${isMobile ? 'px-3 py-2.5' : 'px-4 py-4'}`}>
                                    <div className={`text-gray-500 dark:text-gray-400 whitespace-nowrap ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'}`}>Tiền mặt</div>
                                    <div className={`font-bold text-gray-900 dark:text-white whitespace-nowrap ${isMobile ? 'text-sm' : 'text-base'}`}>
                                        {formatCompactNumber(portfolioData.cashRemaining)}
                                    </div>
                                </div>

                                {/* Phân bổ */}
                                <div className={`flex-1 ${isMobile ? 'px-3 py-2.5' : 'px-4 py-4'}`}>
                                    <div className={`text-gray-500 dark:text-gray-400 whitespace-nowrap ${isMobile ? 'text-[10px] mb-1' : 'text-xs mb-2'}`}>Phân bổ</div>
                                    <div className={`font-bold text-gray-900 dark:text-white whitespace-nowrap ${isMobile ? 'text-sm' : 'text-base'}`}>
                                        {portfolioData.holdingCount} mã
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Treemap View */}
                {viewMode === 'treemap' && treemapOptions && (
                    <div className={isMobile ? 'p-1' : 'p-2'}>
                        <HighchartsReact
                            ref={treemapRef}
                            highcharts={Highcharts}
                            options={treemapOptions}
                        />
                    </div>
                )}

                {/* Table View */}
                {viewMode === 'table' && (
                    <div className="overflow-auto max-h-[250px]">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 dark:border-gray-700">
                                    <th className={`${isMobile ? 'px-2 py-2' : 'px-3 py-2.5'} text-left text-xs font-medium text-gray-500 dark:text-gray-400`}>Mã cổ phiếu</th>
                                    {!isMobile && (
                                        <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 dark:text-gray-400">KL</th>
                                    )}
                                    {!isMobile && (
                                        <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 dark:text-gray-400">
                                            <div>Giá hiện tại</div>
                                            <div className="text-gray-400 dark:text-gray-500 font-normal">Giá vốn</div>
                                        </th>
                                    )}
                                    <th className={`${isMobile ? 'px-2 py-2' : 'px-3 py-2.5'} text-right text-xs font-medium text-gray-500 dark:text-gray-400`}>
                                        <div>Giá trị TT</div>
                                        <div className="text-gray-400 dark:text-gray-500 font-normal">Số tiền mua</div>
                                    </th>
                                    <th className={`${isMobile ? 'px-2 py-2' : 'px-3 py-2.5'} text-right text-xs font-medium text-gray-500 dark:text-gray-400`}>Lãi/lỗ</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                                {holdingsData.map(stock => {
                                    const priceChange = stock.pricePctChgIntraDay || 0;
                                    const currentPrice = stock.price || stock.priceCloseIntraDay || 0;
                                    const volume = stock.stockVolume || 0;
                                    const costPrice = volume > 0 ? (stock.costValue || 0) / volume : 0;
                                    const marketValue = stock.valueEnd || 0;
                                    const costValue = stock.costValue || 0;
                                    const returnValue = stock.totalReturnValue || 0;
                                    const returnPct = stock.totalReturnValuePct || 0;
                                    return (
                                        <tr key={stock.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                            {/* Mã cổ phiếu + Biến động */}
                                            <td className={isMobile ? 'px-2 py-2' : 'px-3 py-2.5'}>
                                                <div className="flex items-center gap-2">
                                                    <img
                                                        src={`https://finance.vietstock.vn/image/${stock.ticker}`}
                                                        alt={stock.ticker}
                                                        className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 object-contain flex-shrink-0"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                    <div>
                                                        {isMobile ? (
                                                            <>
                                                                <div className="font-semibold text-gray-900 dark:text-white">{stock.ticker}</div>
                                                                <div className={`text-xs font-medium ${priceChange >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                                    {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="font-semibold text-gray-900 dark:text-white">{stock.ticker}</span>
                                                                    <span className={`text-xs font-medium ${priceChange >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                                        {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
                                                                    </span>
                                                                </div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[120px]">
                                                                    {stock.companyName || ''}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            {/* Khối lượng - ẩn trên mobile */}
                                            {!isMobile && (
                                                <td className="px-3 py-2.5 text-right text-gray-900 dark:text-white">
                                                    {formatCurrency(volume)}
                                                </td>
                                            )}
                                            {/* Giá hiện tại / Giá vốn - ẩn trên mobile */}
                                            {!isMobile && (
                                                <td className="px-3 py-2.5 text-right">
                                                    <div className="text-gray-900 dark:text-white">{formatCurrency(currentPrice)}</div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">{formatCurrency(Math.round(costPrice))}</div>
                                                </td>
                                            )}
                                            {/* Giá trị thị trường / Số tiền mua */}
                                            <td className={`${isMobile ? 'px-2 py-2' : 'px-3 py-2.5'} text-right`}>
                                                <div className="text-gray-900 dark:text-white">{formatCompactNumber(marketValue)}</div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">{formatCompactNumber(costValue)}</div>
                                            </td>
                                            {/* Lãi/lỗ */}
                                            <td className={`${isMobile ? 'px-2 py-2' : 'px-3 py-2.5'} text-right`}>
                                                <div className={`font-medium ${returnValue >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                    {returnValue >= 0 ? '+' : ''}{formatCompactNumber(returnValue)}
                                                </div>
                                                <div className={`text-xs ${returnPct >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                                    {returnPct >= 0 ? '+' : ''}{returnPct.toFixed(2)}%
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Warning Section */}
                {portfolioData.returnWarningCashs?.length > 0 && (
                    <div className={`${isMobile ? 'mx-2 mb-2' : 'mx-4 mb-3'} rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 px-3 py-2`}>
                        <div className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                                <p>
                                    Danh mục có {portfolioData.returnWarningCashs[0]?.negativeCashCount || 0} ngày số dư tiền mặt bị âm,
                                    bắt đầu từ ngày {portfolioData.returnWarningCashs[0]?.date || '-'} với số dư là {formatCompactNumber(portfolioData.returnWarningCashs[0]?.amount || 0)}.
                                </p>
                                <p className="mt-1">
                                    Hãy kiểm tra lại nhật ký giao dịch nhé! Có thể bạn thiếu giao dịch{' '}
                                    {portfolioData.type !== 'INTEGRATION' ? (
                                        <a
                                            href={`https://simplize.vn/portfolio/${portfolioData.id}/nhat-ky-dau-tu`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 dark:text-blue-400 underline"
                                        >
                                            nộp tiền
                                        </a>
                                    ) : (
                                        <span>nộp tiền</span>
                                    )}
                                    , hoặc giao dịch nào đó đang bị{' '}
                                    <a
                                        href={`https://simplize.vn/portfolio/${portfolioData.id}/nhat-ky-dau-tu`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 dark:text-blue-400 underline"
                                    >
                                        sai thông tin
                                    </a>
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Footer Link */}
                <div className={`${isMobile ? 'py-2' : 'py-3'} flex justify-center`}>
                    <a
                        href={`https://simplize.vn/portfolio/${portfolioData.id}/tong-quan`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                    >
                        Xem chi tiết trên Simplize
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                    </a>
                </div>
            </div>
        </div>
    );
}
